import { TallyEntry } from '../../types/tally';

const formatTallyDate = (dateString: string): string => {
  if (!dateString) return '';

  try {
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
      const [day, month, year] = dateString.split('/');
      const monthNames = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ];
      return `${day}-${monthNames[parseInt(month, 10) - 1]}-${year}`;
    }

    if (!isNaN(Number(dateString))) {
      const excelEpoch = new Date(1899, 11, 30);
      const msPerDay = 24 * 60 * 60 * 1000;
      const excelDate = new Date(
        excelEpoch.getTime() + Number(dateString) * msPerDay,
      );

      const day = excelDate.getDate().toString().padStart(2, '0');
      const monthNames = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ];
      const month = monthNames[excelDate.getMonth()];
      const year = excelDate.getFullYear();

      return `${day}-${month}-${year}`;
    }

    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      const day = date.getDate().toString().padStart(2, '0');
      const monthNames = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ];
      const month = monthNames[date.getMonth()];
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    }

    const dateMatch = dateString.match(/(\d{1,2})[-\/](\w{3})[-\/](\d{2,4})/);
    if (dateMatch) {
      const day = dateMatch[1].padStart(2, '0');
      const month = dateMatch[2];
      let year = dateMatch[3];
      if (year.length === 2) {
        year = parseInt(year) > 50 ? `19${year}` : `20${year}`;
      }
      return `${day}-${month}-${year}`;
    }

    return dateString;
  } catch (error) {
    return dateString;
  }
};

function isCommonLedger(name: string): boolean {
  if (!name) return false;
  const upperName = name.toUpperCase();
  const commonLedgers = [
    'TDS',
    'GST',
    'CGST',
    'SGST',
    'IGST',
    'INPUT',
    'TAX',
    'DISCOUNT',
    'ROUND',
    'FREIGHT',
    'BANK',
    'CASH',
    'CAPITAL',
    'SALARY',
    'RENT',
  ];
  return commonLedgers.some(ledger => upperName.includes(ledger));
}

const processVoucher = (
  rows: any[],
  voucherTypeKey: string,
  typeOfLedgerKey: string,
  ledgerNameKey: string,
  amountKey: string,
  itemNameKey: string,
  narrationKey: string,
  customerNameKey: string,
  quantityKey: string,
  unitKey: string,
  rateKey: string,
  invoiceDateKey: string,
  voucherDateKey: string,
  voucherNumberKey: string,
): TallyEntry | null => {
  const voucherTypeRaw = (rows[0][voucherTypeKey] || 'Purchase').toString().toLowerCase();
  const isSales = voucherTypeRaw.includes('sales');

  const partyLedgerType = isSales ? 'dr' : 'cr';
  const oppositeLedgerType = isSales ? 'cr' : 'dr';
  const mainLedgerName = isSales ? 'Sales' : 'Purchase';

  let partyEntry: any = null;
  let tdsEntry: any = null;
  let roundOffEntry: any = null;

  const explicitPartyEntries = rows.filter(row => {
    const typeOfLedger = (row[typeOfLedgerKey] || '').toString().toLowerCase();
    return typeOfLedger === partyLedgerType;
  });

  tdsEntry = explicitPartyEntries.find(row => {
    const ledgerName = (row[ledgerNameKey] || '').toString().toUpperCase();
    return ledgerName.includes('TDS');
  });

  roundOffEntry = rows.find(row => {
    const ledgerName = (row[ledgerNameKey] || '').toString().toUpperCase();
    return ledgerName.includes('ROUND');
  });

  if (explicitPartyEntries.length > 0) {
    const nonTdsEntries = explicitPartyEntries.filter(row => {
      const ledgerName = (row[ledgerNameKey] || '').toString().toUpperCase();
      return !ledgerName.includes('TDS') && !ledgerName.includes('ROUND');
    });

    if (nonTdsEntries.length === 1) {
      partyEntry = nonTdsEntries[0];
    } else if (nonTdsEntries.length > 1) {
      partyEntry = nonTdsEntries.reduce((prev, current) => {
        const prevAmount = Math.abs(parseFloat(prev[amountKey] || '0'));
        const currentAmount = Math.abs(parseFloat(current[amountKey] || '0'));
        return prevAmount > currentAmount ? prev : current;
      }, nonTdsEntries[0]);
    }
  }

  if (!partyEntry && rows.length > 0) {
    partyEntry = rows[0];
  }

  const inventoryItems = rows.filter(row => {
    if (row === partyEntry || row === tdsEntry) return false;
    const typeOfLedger = (row[typeOfLedgerKey] || '').toString().toLowerCase();
    if (typeOfLedger !== oppositeLedgerType) return false;
    
    const ledgerName = row[ledgerNameKey] || '';
    const itemName = row[itemNameKey] || '';

    if (isSales) {
        return itemName.trim() !== '';
    } else {
        return (
            ((!ledgerName || ledgerName.trim() === '') &&
              itemName.trim() !== '') ||
            (itemName && !isCommonLedger(ledgerName))
        );
    }
  });

  const regularEntries = rows.filter(row => {
    if (row === partyEntry || row === tdsEntry || row === roundOffEntry) return false;
    const typeOfLedger = (row[typeOfLedgerKey] || '').toString().toLowerCase();
    if (typeOfLedger !== oppositeLedgerType) return false;
    const itemName = row[itemNameKey] || '';
    const ledgerName = row[ledgerNameKey] || '';
    const isRoundOffLedger = ledgerName.toUpperCase().includes('ROUND');

    if (isRoundOffLedger) return false;
    
    if (isSales) {
        return itemName.trim() === '';
    } else {
        return (
            !(!ledgerName || ledgerName.trim() === '') || itemName.trim() === ''
        );
    }
  });

  const invoiceDate = formatTallyDate(rows[0][invoiceDateKey] || '');
  const voucherDate = formatTallyDate(rows[0][voucherDateKey] || '');
  const partyAmount = parseFloat(partyEntry?.[amountKey] || '0');
  const tdsAmount = parseFloat(tdsEntry?.[amountKey] || '0');
  const partyLedger = partyEntry?.[ledgerNameKey] || '';
  const customerName = rows[0][customerNameKey] || '';

  const mainLedgerItems: any[] = [];
  const oppositeLedgerItems: any[] = [];

  regularEntries.forEach(item => {
    const amount = parseFloat(item[amountKey] || '0');
    mainLedgerItems.push({
      ledgerName: item[ledgerNameKey] || mainLedgerName,
      amount: Math.abs(amount),
    });
  });

  if (inventoryItems.length > 0) {
    const totalInventoryAmount = inventoryItems.reduce(
      (sum, item) => sum + Math.abs(parseFloat(item[amountKey] || '0')),
      0,
    );
    const inventoryItem = {
      ledgerName: mainLedgerName,
      amount: totalInventoryAmount,
      isInventoryItem: true,
      inventoryAllocations: inventoryItems.map(item => {
        const amount = parseFloat(item[amountKey] || '0');
        let quantity = item[quantityKey] ? parseFloat(item[quantityKey]) : 1;
        let rate = item[rateKey] ? parseFloat(item[rateKey]) : Math.abs(amount) / quantity;
        let unit = item[unitKey] || 'NOS';
        return {
          itemName: item[itemNameKey] || 'Inventory Item',
          amount: Math.abs(amount),
          quantity: quantity,
          unit: unit,
          rate: rate,
        };
      }),
    };
    mainLedgerItems.push(inventoryItem);
  }

  const discountEntries = rows.filter(row => {
    const ledgerName = (row[ledgerNameKey] || '').toString().toUpperCase();
    return ledgerName.includes('DISCOUNT');
  });

  discountEntries.forEach(discountEntry => {
    const discountAmount = Math.abs(parseFloat(discountEntry[amountKey] || '0'));
    if (discountAmount > 0) {
      oppositeLedgerItems.push({
        ledgerName: discountEntry[ledgerNameKey] || 'Discount',
        amount: discountAmount,
      });
    }
  });

  if (roundOffEntry) {
    const roundOffAmount = Math.abs(parseFloat(roundOffEntry[amountKey] || '0'));
    const roundOffType = (roundOffEntry[typeOfLedgerKey] || '').toString().toLowerCase();

    if (roundOffType === 'dr') {
      (isSales ? oppositeLedgerItems : mainLedgerItems).push({
        ledgerName: roundOffEntry[ledgerNameKey] || 'Round Off',
        amount: roundOffAmount,
        isInventoryItem: false,
      });
    } else if (roundOffType === 'cr') {
      (isSales ? mainLedgerItems : oppositeLedgerItems).push({
        ledgerName: roundOffEntry[ledgerNameKey] || 'Round Off',
        amount: roundOffAmount,
      });
    }
  }

  const entry: TallyEntry = {
    voucherDate: voucherDate,
    invoiceDate: invoiceDate,
    voucherType: isSales ? 'Sales' : voucherTypeRaw,
    voucherNumber: (rows[0][voucherNumberKey] || '').toString(),
    reference: `Inv #${rows[0][voucherNumberKey] || ''}`,
    narration: partyEntry?.[narrationKey] || `Invoice #${rows[0][voucherNumberKey] || ''}`,
    creditLedger: partyLedger,
    creditAmount: Math.abs(partyAmount),
    partyLedgerType: isSales ? 'Dr' : 'Cr',
    tdsLedger: tdsEntry?.[ledgerNameKey] || '',
    tdsAmount: Math.abs(tdsAmount),
    debitItems: isSales ? [] : mainLedgerItems,
    creditItems: isSales ? mainLedgerItems : oppositeLedgerItems,
    customerName: customerName,
  };

  if (isSales) {
    entry.debitItems = [];
    entry.creditItems = mainLedgerItems;
  } else {
    entry.debitItems = mainLedgerItems;
    entry.creditItems = oppositeLedgerItems;
  }

  return entry;
};

export const parseExcelData = (excelData: any[]): TallyEntry[] => {
  if (!Array.isArray(excelData) || excelData.length === 0) {
    return [];
  }

  const standardizeColumnName = (name: string): string => {
    if (!name) return '';
    return name.toString().toLowerCase().replace(/\s+/g, '');
  };

  const getColumnKey = (
    data: any[],
    standardColumnName: string,
  ): string | null => {
    for (const key of Object.keys(data[0] || {})) {
      if (standardizeColumnName(key) === standardColumnName) {
        return key;
      }
    }

    for (const key of Object.keys(data[0] || {})) {
      const stdKey = standardizeColumnName(key);

      if (
        standardColumnName === 'voucherdate' &&
        (stdKey.includes('date') || stdKey.includes('voucher'))
      ) {
        return key;
      }
      if (
        standardColumnName === 'invoicedate' &&
        (stdKey.includes('date') || stdKey.includes('voucher'))
      ) {
        return key;
      }

      if (
        standardColumnName === 'vouchername' &&
        (stdKey.includes('name') || stdKey.includes('voucher'))
      ) {
        return key;
      }

      if (
        standardColumnName === 'invoicenumber' &&
        (stdKey.includes('number') ||
          stdKey.includes('invoice') ||
          stdKey.includes('voucher'))
      ) {
        return key;
      }

      if (
        standardColumnName === 'ledgername' &&
        stdKey.includes('ledger') &&
        stdKey.includes('name')
      ) {
        return key;
      }

      if (
        standardColumnName === 'typeofledger' &&
        (stdKey.includes('type') ||
          stdKey.includes('dr') ||
          stdKey.includes('cr'))
      ) {
        return key;
      }

      if (
        standardColumnName === 'amount' &&
        (stdKey.includes('amount') || stdKey.includes('value'))
      ) {
        return key;
      }

      if (
        standardColumnName === 'customerocr' &&
        stdKey.includes('customer') &&
        stdKey.includes('ocr')
      ) {
        return key;
      }

      if (
        standardColumnName === 'quantity' &&
        (stdKey.includes('qty') || stdKey.includes('quantity'))
      ) {
        return key;
      }

      if (
        standardColumnName === 'unit' &&
        (stdKey.includes('unit') ||
          stdKey.includes('uom') ||
          stdKey.includes('measure'))
      ) {
        return key;
      }

      if (
        standardColumnName === 'rate' &&
        (stdKey.includes('rate') ||
          stdKey.includes('cost') ||
          stdKey.includes('price'))
      ) {
        return key;
      }
    }

    return null;
  };

  const invoiceDateKey =
    getColumnKey(excelData, 'InvoiceDate') || 'Invoice Date';
  const voucherDateKey =
    getColumnKey(excelData, 'VoucherDate') || 'Voucher Date';
  const voucherNumberKey =
    getColumnKey(excelData, 'invoicenumber') || 'Invoice Number';
  const ledgerNameKey = getColumnKey(excelData, 'ledgername') || 'Ledger Name';
  const typeOfLedgerKey =
    getColumnKey(excelData, 'typeofledger') || 'Type Of Ledger (Dr/Cr)';
  const amountKey = getColumnKey(excelData, 'amount') || 'Ledger Amount';
  const quantityKey = getColumnKey(excelData, 'quantity') || 'Quantity';
  const unitKey = getColumnKey(excelData, 'unit') || 'UnitOfMeasurement';
  const rateKey = getColumnKey(excelData, 'rate') || 'Unit Cost';
  const itemNameKey = getColumnKey(excelData, 'itemname') || 'Item Name';
  const narrationKey = getColumnKey(excelData, 'narration') || 'Narration';
  const customerNameKey =
    getColumnKey(excelData, 'customername') || 'CustomerName';
  const voucherTypeKey =
    getColumnKey(excelData, 'vouchername') || 'Voucher Name';

  const entriesByVoucher = new Map<string, any[]>();

  excelData.forEach(row => {
    const invoiceDate = row[invoiceDateKey] || '';
    const voucherNumber = row[voucherNumberKey] || '';

    if (invoiceDate && voucherNumber) {
      const voucherKey = `${voucherNumber}_${invoiceDate}`;

      if (!entriesByVoucher.has(voucherKey)) {
        entriesByVoucher.set(voucherKey, []);
      }

      entriesByVoucher.get(voucherKey)?.push(row);
    }
  });

  const tallyEntries: TallyEntry[] = [];

  entriesByVoucher.forEach((rows, voucherKey) => {
    try {
      const entry = processVoucher(
        rows,
        voucherTypeKey,
        typeOfLedgerKey,
        ledgerNameKey,
        amountKey,
        itemNameKey,
        narrationKey,
        customerNameKey,
        quantityKey,
        unitKey,
        rateKey,
        invoiceDateKey,
        voucherDateKey,
        voucherNumberKey,
      );
      if (entry) {
        tallyEntries.push(entry);
      }
    } catch (error) {
      console.error(`Error processing voucher ${voucherKey}:`, error);
    }
  });

  return tallyEntries;
};